import {
  Anchor,
  AppShell,
  Blockquote,
  Button,
  Flex,
  Group,
  Loader,
  Modal,
  Text,
} from "@mantine/core";
import { useGetVentHomeQuery } from "../slices/api/ventsApi";
import { useEffect } from "react";
import {
  displayErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";
import { useGetUsersGuestIdForVentQuery } from "../slices/api/guestsApi";
import { useLocation } from "wouter";
import {
  formatServerToLocalDate,
  formatServerToLocalDateAsTimeOnly,
  formatServerToLocalDateTz,
  serverToLocalDate,
} from "../shared/dates";
import { useAuth } from "../authentication/AuthProvider";
import dayjs from "dayjs";
import { IconInfo, IconRsvp } from "../shared/components/Icons";
import { useDownloadCalendarMutation } from "../slices/api/downloadsApi";
import { getLinkToVentGuest } from "../shared/links";
import ConnectGuest from "./ConnectGuest";
import { useDisclosure } from "@mantine/hooks";

interface Props {
  ventId: string;
  setTabDescription: (name: string) => void;
}

/*
If the user is not logged in, then we take them to the signin/up page when they try to RSVP.
If the user is logged in, but not connected, then we connect them and take them to the RSVP page.
If the user is logged in and connected, then we take them to the RSVP page.
*/

const VentHomeTab = (props: Props) => {
  const ventId = props.ventId;
  const {
    data: ventResponse,
    isFetching: isFetchingVent,
    isError: isErrorFetchingVent,
    error: ventError,
  } = useGetVentHomeQuery({
    id: ventId,
  });
  useEffect(() => {
    if (ventResponse) {
      props.setTabDescription(ventResponse.name);
    }
  }, [ventResponse]);
  const [location, navigate] = useLocation();
  const authContext = useAuth();
  const { data: getUsersGuestIdResponse } = useGetUsersGuestIdForVentQuery(
    {
      ventId: ventId,
    },
    {
      skip: authContext?.user == null,
    },
  );
  const [
    openedConnectGuest,
    { open: openConnectGuest, close: closeConnectGuest },
  ] = useDisclosure(false);

  const [downloadCalendar] = useDownloadCalendarMutation();
  const handleDownloadCalendar = async () => {
    let objectUrl: string | null = null; // Variable to hold the object URL for cleanup
    try {
      await downloadCalendar({
        ventId: props.ventId,
      }).unwrap();
    } catch (error) {
      console.log(error);
      displayErrorMessage("Download failed");
      return;
    } finally {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        console.log("Revoked Object URL:", objectUrl); // For debugging
      }
    }
  };

  if (isErrorFetchingVent) {
    handleFetchingError(ventError);
    return null;
  }
  if (isFetchingVent || !ventResponse) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }

  let endsText = "";
  if (ventResponse?.ends) {
    const startDate = dayjs(serverToLocalDate(ventResponse.starts));
    const endDate = dayjs(serverToLocalDate(ventResponse.ends));
    if (endDate.isSame(startDate, "day")) {
      endsText = " to " + formatServerToLocalDateAsTimeOnly(ventResponse.ends);
    } else {
      endsText = " to " + formatServerToLocalDate(ventResponse.ends);
    }
  }

  let descriptionHtml = "";
  if (ventResponse.descriptionHtml) {
    descriptionHtml = ventResponse.descriptionHtml.replace(/\n/g, "<br />");
  }

  let ventLocation = "";
  if (ventResponse.location) {
    ventLocation = ventResponse.location.replace(/\n/g, "<br />");
  }

  const tokens: string[] = [
    "Clicking RSVP will connect you to the event and open the RSVP tab where you can manage your response",
  ];
  if (ventResponse.allowGuestsView || ventResponse.plusOneMax > 0) {
    tokens.push(", send a message to the host");
  } else {
    tokens.push(" and send a message to the host");
  }
  const both = ventResponse.allowGuestsView && ventResponse.plusOneMax > 0;
  if (ventResponse.allowGuestsView) {
    if (both) {
      tokens.push(", ");
    } else {
      tokens.push(" and ");
    }
    tokens.push("see who is coming");
  }
  if (ventResponse.plusOneMax > 0) {
    if (!both) {
      tokens.push(" and ");
    } else {
      tokens.push(", and ");
    }
    tokens.push("invite plus ones");
  }
  tokens.push(".");
  if (ventResponse.hasActivities) {
    tokens.push(" You may also sign up for activities.");
  }
  const notesText = tokens.join("");

  const rsvpStartDate = dayjs(serverToLocalDate(ventResponse.rsvpStart));
  const rsvpEndDate = dayjs(serverToLocalDate(ventResponse.rsvpEnd));
  const now = dayjs();
  const isEarly = now.isBefore(rsvpStartDate);
  const isLate = now.isAfter(rsvpEndDate);
  const isOpen = !isEarly && !isLate;
  let closesText = "";
  if (isOpen) {
    closesText = `Changes must be made by ${formatServerToLocalDateTz(ventResponse?.rsvpEnd)}.`;
  }

  return (
    <>
      <AppShell.Main>
        <Modal
          opened={openedConnectGuest}
          onClose={closeConnectGuest}
          withCloseButton={false}
          centered
          size="lg"
        >
          <ConnectGuest
            onClose={closeConnectGuest}
            ventId={ventId}
            ventName={ventResponse.name}
            ventHasCustomFields={ventResponse.hasCustomFields}
            ventAllowsPlusOnes={ventResponse.plusOneMax > 0}
            ventHasActivities={ventResponse.hasActivities}
          />
        </Modal>
        <Flex justify="center" align="center" direction="column">
          <Text size="xl">{ventResponse.name}</Text>
          <Text size="xl">
            {formatServerToLocalDate(ventResponse.starts) + endsText}
          </Text>
          {authContext.user && (
            <Anchor onClick={() => handleDownloadCalendar()} component="a">
              Download Calendar Invitation
            </Anchor>
          )}
          <Text
            size="xl"
            component="div"
            style={{
              lineHeight: 1,
              textAlign: "center",
            }}
            dangerouslySetInnerHTML={{
              __html: ventLocation,
            }}
          />
          {ventResponse.locationUrl && (
            <Anchor href={ventResponse.locationUrl} target="_blank">
              Map
            </Anchor>
          )}
          {authContext.user && (
            <Group>
              <Button
                mt="md"
                onClick={() => {
                  // If the user is already connected, then we just navigate to the RSVP page.
                  const guestId = getUsersGuestIdResponse?.guestId ?? "";
                  if (guestId !== "") {
                    navigate(getLinkToVentGuest(props.ventId, guestId));
                    return;
                  }
                  // Not connected, so open connectGuest modal.
                  if (!isOpen) {
                    displayErrorMessage(
                      "The event is not open for RSVPs at this time.",
                    );
                    return;
                  }
                  openConnectGuest();
                }}
                rightSection={<IconRsvp />}
              >
                RSVP
              </Button>
            </Group>
          )}
          {isEarly && (
            <Text size="xl">
              This event will not be open for RSVPs until{" "}
              {formatServerToLocalDate(ventResponse.rsvpStart)}
            </Text>
          )}
          {isLate && (
            <Text size="xl">
              To make changes please contact the host. The event is no longer
              open for making changes.
            </Text>
          )}
          {authContext.user == null && (
            <Button
              mt="md"
              onClick={() => {
                localStorage.setItem("intendedUrl", location);
                navigate(`/login`);
              }}
            >
              Login
            </Button>
          )}
        </Flex>
        <Text
          mt="lg"
          component="div"
          style={{ lineHeight: 1 }}
          dangerouslySetInnerHTML={{
            __html: descriptionHtml,
          }}
        />
        <Blockquote
          color="blue"
          icon={<IconInfo />}
          hidden={authContext.user != null}
          mt="xl"
        >
          Once you log in you will be able to RSVP. By logging in you will be
          able to make changes later.
        </Blockquote>
        <Blockquote
          color="blue"
          icon={<IconInfo />}
          hidden={!authContext.user}
          mt="xl"
        >
          {notesText} {closesText}
        </Blockquote>
      </AppShell.Main>
    </>
  );
};

export default VentHomeTab;
